// src/services/viewerProfileService.ts
import unifiedAuth from '../utils/unifiedAuth';
import { config } from '../config';

/** The ratings a profile can be limited to. Ordered, because a limit means "and below". */
export const MATURITY_RATINGS = ['U', 'PG', '12', '15', '18'] as const;
export type MaturityRating = (typeof MATURITY_RATINGS)[number];

export interface ViewerProfile {
  id: string;
  name: string;
  avatarUrl?: string | null;
  isDefault: boolean;
  displayOrder: number;
  /**
   * How far this profile may watch, or null for no profile limit.
   *
   * Only ever narrows. The account's own age check still applies and the stricter of the
   * two wins, so setting this to 18 on a child's account unlocks nothing.
   */
  maxRating?: MaturityRating | null;
}

function apiUrl(path = ''): string {
  const base = (config.apiBaseUrl || '').replace(/\/+$/, '');
  return base.endsWith('/api') ? `${base}/profiles${path}` : `${base}/api/profiles${path}`;
}

/**
 * Which profile this browser is watching as.
 *
 * <p>Held in localStorage rather than in React state, because every watch-progress
 * request needs it and threading it through every caller would mean a prop on every
 * player. Read at request time, so a switch takes effect on the next call without
 * anything having to re-render first.
 *
 * <p>Per browser, deliberately. Two people in a household are usually two devices, and
 * remembering the choice per device is the difference between picking a profile once and
 * picking it on every visit.
 */
const ACTIVE_PROFILE_KEY = 'strymhub_active_profile';

export function getActiveProfileId(): string | null {
  // Wrapped because localStorage throws in a private window and returns nothing with
  // site data cleared. A viewer with no readable storage watches as the default
  // profile, which is the same place their history already was.
  try {
    return localStorage.getItem(ACTIVE_PROFILE_KEY);
  } catch {
    return null;
  }
}

export function setActiveProfileId(id: string | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(ACTIVE_PROFILE_KEY);
    } else {
      localStorage.setItem(ACTIVE_PROFILE_KEY, id);
    }
  } catch {
    // A viewer who cannot persist the choice keeps watching; they just pick again next
    // visit. Failing the switch would be worse than forgetting it.
  }
}

/**
 * The header that tells the server which viewer a request is for.
 *
 * <p>Omitted entirely when nothing is chosen, rather than sent empty: the server treats
 * an absent header as the account's default profile, which is where all history from
 * before profiles existed lives.
 */
export function activeProfileHeader(): Record<string, string> {
  const id = getActiveProfileId();
  return id ? { 'X-Viewer-Profile': id } : {};
}

async function unwrap<T>(response: Response): Promise<T> {
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.message || `HTTP error! status: ${response.status}`);
  }
  return (json?.data ?? null) as T;
}

export async function listProfiles(): Promise<ViewerProfile[]> {
  const response = await unifiedAuth.fetch(apiUrl(), { method: 'GET' });
  return (await unwrap<ViewerProfile[]>(response)) ?? [];
}

export async function createProfile(name: string, avatarUrl?: string): Promise<ViewerProfile> {
  const response = await unifiedAuth.fetch(apiUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, avatarUrl }),
  });
  return unwrap<ViewerProfile>(response);
}

export async function renameProfile(id: string, name: string): Promise<ViewerProfile> {
  const response = await unifiedAuth.fetch(apiUrl(`/${encodeURIComponent(id)}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    // Name only. The server leaves an omitted field alone, so renaming does not clear
    // a maturity limit and setting a limit does not rename anything.
    body: JSON.stringify({ name }),
  });
  return unwrap<ViewerProfile>(response);
}

/**
 * Changes how far a profile may watch.
 *
 * An empty string lifts the limit, which is the same call rather than a separate one -
 * a parent who cannot undo a restriction has a worse problem than one who cannot set it.
 */
export async function setProfileMaturity(
  id: string,
  maxRating: MaturityRating | '',
): Promise<ViewerProfile> {
  const response = await unifiedAuth.fetch(apiUrl(`/${encodeURIComponent(id)}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ maxRating }),
  });
  return unwrap<ViewerProfile>(response);
}

/** Removes a profile and everything it had watched. Refused for the last or the main one. */
export async function deleteProfile(id: string): Promise<void> {
  const response = await unifiedAuth.fetch(apiUrl(`/${encodeURIComponent(id)}`), { method: 'DELETE' });
  await unwrap<unknown>(response);

  // If the browser was watching as the profile that just went, stop pointing at it -
  // the server would refuse every request naming an id that no longer exists.
  if (getActiveProfileId() === id) {
    setActiveProfileId(null);
  }
}
